import cx from 'classnames'
import { useMemo } from 'react'
import { formatAggressorLLPrice } from '../../helpers/formatting'
import { QuoteRequestWithBestResponse } from '../../store/rfq/types'
import {
  formatSpread,
  orderTypeToOppositeAggressLabel
} from '../DepthOfMarket/helpers'
import FlashingCircleAfter from '../Effects/FlashingCircleAfter'
import { useExpiration } from '../Timer/helpers'
import {
  getDisplayStatus as backendToFrontendStatus,
  quoteStatusIsInactive,
  statusMap
} from './utils'

import countdownStyles from '../Timer/countdown.module.css'
import styles from './rfq.module.css'

export interface CardProps {
  rfq: QuoteRequestWithBestResponse
  currency: string
  isTreasury: boolean
  accept: (quoteId: number) => void
  reject: () => void
  className?: string
  testIdPrefix?: string
}

const getDisplayStatus = (status: string) => {
  const displayStatus = statusMap[status]
  return displayStatus ?? status
}

const QuoteCard = ({
  rfq,
  currency,
  isTreasury,
  accept,
  reject,
  className,
  testIdPrefix
}: CardProps) => {
  const { bestResponse, isSpread, exposureDuration } = rfq
  const quote = bestResponse
  const subjectLabel = orderTypeToOppositeAggressLabel(rfq.type)
  const priceLabel = `${rfq.type === 'buy' ? 'Offer:' : 'Bid:'}`

  const allInValue = useMemo(() => {
    return isSpread
      ? formatSpread(quote.markedSpread)
      : formatAggressorLLPrice(quote.markedPrice, currency, isTreasury)
  }, [isSpread, currency, quote.markedPrice, quote.markedSpread])

  const disableButtons = quoteStatusIsInactive(quote) || rfq.isExpired
  const stopped = disableButtons
  const status = backendToFrontendStatus(quote)
  const [acceptLabel, rejectLabel] = useMemo(() => {
    if (!['Subject', 'HitLiftExpire'].includes(status)) {
      return ['Accept', 'Reject']
    }
    return [subjectLabel]
  }, [status, subjectLabel])

  const displayStatus = getDisplayStatus(status)

  const timerDuration = useMemo(() => {
    const validLength = quote.expireTime.valueOf() - new Date().valueOf()
    return Math.max(validLength / 1000, exposureDuration, 0)
  }, [exposureDuration, quote.expireTime])

  const { displayTime, timeToExpiration, barStyle, fraction } = useExpiration(
    timerDuration,
    quote.expireTime,
    stopped
  )
  const overrides = useMemo(
    () => ({
      ...barStyle,
      ['--countdownColor']: 'var(--rfqColor)'
    }),
    [barStyle]
  )
  const countdownClasses = cx(
    styles.shotClock,
    !timeToExpiration && styles.timedOut
  )
  return (
    <div className={cx(styles.quoteCard, className)}>
      <div className={cx(styles.fields)}>
        <div className={styles.priceContainer}>
          {priceLabel}
          <em className={styles.price}>
            <FlashingCircleAfter
              content={`${rfq.size.toLocaleString()} @ ${allInValue}`}
              active={displayStatus === 'Firm'}
            />
          </em>
        </div>
        <div className={styles.status}>
          Status:{' '}
          <em className={status === 'Traded' ? styles.traded : ''}>
            {displayStatus}
          </em>
        </div>
        {quote.firmName && <div>Firm: {quote.firmName}</div>}
        {quote.traderName && <div>Trader: {quote.traderName}</div>}
        <div
          className={cx(
            countdownStyles.countdownBar,
            !stopped && fraction > 0 && countdownStyles.running
          )}
          style={overrides}
        >
          Firm for:{' '}
          <span>
            {status === 'New' ? (
              <span className={countdownClasses}>{displayTime}</span>
            ) : (
              'N/A'
            )}
          </span>
        </div>
      </div>
      <div className={styles.controls}>
        {!disableButtons && (
          <>
            <button
              data-testid={`${testIdPrefix}-quote-${quote.id}-accept`}
              onClick={() => accept(quote.id)}
              disabled={disableButtons}
              className="confirm"
            >
              {acceptLabel}
            </button>
            {!!rejectLabel && (
              <button
                data-testid={`${testIdPrefix}-quote-${quote.id}-reject`}
                onClick={reject}
                disabled={disableButtons}
              >
                {rejectLabel}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default QuoteCard
